import { AppContext, BookingData } from "../../../../AppDataProvider/AppDataProvider";
import { Input as BaseInput, Error, ErrorConnector, HelpText, Label, RequiredStar } from "../common";
import { corners, marginBottom, marginRight, marginTop, media, padding, typography } from "../../../styles";
import { CreateParticipantUploadPresignedUrlInput, useCreatePostDataMutation } from "../../../../GeneratedGraphQL";
import React, { useContext, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { applyOpacity } from "../../../styles/utils";
import { Button } from "../../Button/Button";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getPostBody } from "./utils/getPostBody";
import { LoadingSpinner } from "../../LoadingSpinner/LoadingSpinner";
import { PartRequired } from "../../../../utils/types";
import { requireBookingDataFields } from "../../../../utils";
import { SharedInputProps } from "../Input";
import { useTranslation } from "react-i18next";
export type PostData = {
  fields: {
    bucket: string;
    key: string;
    Policy: string;
    "X-Amz-Algorithm": string;
    "X-Amz-Credential": string;
    "X-Amz-Date": string;
    "X-Amz-Signature": string;
  };
  url: string;
};

// These should be the same values as the matching variables in the
// createParticipantUploadPresignedUrl resolver on the back end.
export const SUPPORTED_FILE_EXTENSIONS = [".csv", ".doc", ".docx", ".gif", ".jpeg", ".jpg", ".pdf", ".png", ".txt", ".xls", ".xlsx"];
const MAX_FILE_SIZE = 5242880; //  5MB

export type FileProps = React.SelectHTMLAttributes<HTMLInputElement> & SharedInputProps & {
  fileName?: string;
  onFileUpload: (key: string, fileName: string) => void;
  onRemoveFile: () => void;
  onStartUpload: () => void;
  onStopUpload: () => void;
  setError: (error: string) => void;
  value?: string;
};
export function File({
  error,
  label,
  required,
  id,
  value,
  onFileUpload,
  onStartUpload,
  onStopUpload,
  onRemoveFile,
  setError,
  ...props
}: FileProps): React.ReactElement {
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [fileKey, setFileKey] = useState<string | undefined>(value);
  const [fileName, setFileName] = useState<string | undefined>(props.fileName);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [createPostData] = useCreatePostDataMutation({
    errorPolicy: "all"
  });
  type FileBookingData = PartRequired<BookingData, "organisation" | "reservedSpaceId">;
  const {
    bookingData
  } = useContext(AppContext).appData;
  requireBookingDataFields<FileBookingData>(bookingData, ["organisation", "reservedSpaceId"]);
  const {
    organisation,
    reservedSpaceId
  } = bookingData;

  // Valid file types
  const accept = props.accept ?? SUPPORTED_FILE_EXTENSIONS.join();
  const validateFile = ({
    size
  }: File, extension: string): string | undefined => {
    if (size > MAX_FILE_SIZE) return t("errors.fileTooLarge", {
      max: "5MB"
    });
    if (!accept.includes(extension)) return t("errors.invalidFileExtension", {
      extensions: accept.replace(/,/g, ", ")
    });
  };
  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    // Should not be possible, but just in case
    if (!event.target.files?.length) return;

    // Because we're delaying onFileUpload to happen after validation and
    // upload, manually clear errors when we start the process.
    setError("");

    // Validation
    const file = event.target.files[0];
    const extension = file.name.slice(file.name.lastIndexOf("."));
    const validationError = validateFile(file, extension);
    if (validationError) {
      setError(validationError);
      event.target.value = "";
      return;
    }
    setLoading(true);
    setFileName(file.name);
    onStartUpload();

    // Create and parse presigned POST data
    const createPostDataInput: CreateParticipantUploadPresignedUrlInput = {
      fileExtension: extension.slice(1),
      // resolver needs extension with no "."
      organisationId: organisation._id,
      reservationId: reservedSpaceId
    };
    const {
      data: rawPostData,
      errors: createPostDataErrors
    } = await createPostData({
      variables: {
        input: createPostDataInput
      }
    });
    if (createPostDataErrors || !rawPostData) {
      event.target.value = "";
      setError(t("errors.fileUploadGeneric"));
      onStopUpload();
      setLoading(false);
      return;
    }
    const postData = (JSON.parse(rawPostData.createParticipantUploadPresignedUrl) as PostData);
    await fetch(postData.url, {
      body: getPostBody(file, postData),
      headers: {
        Accept: "application/xml"
      },
      method: "POST"
    }).then(() => {
      onFileUpload(postData.fields.key, file.name);
      setFileKey(postData.fields.key);
    }).catch(() => {
      setError("errors.fileUploadGeneric");
    }).finally(() => {
      onStopUpload();
      setLoading(false);
    });
  };
  const inputProps = {
    accept,
    error: !!error,
    onChange,
    required: true
  };
  const hasError = !!error;
  return <>
      <Label data-sentry-element="Label" data-sentry-source-file="File.tsx">
        {label}
        {required ? <RequiredStar /> : null}
      </Label>
      <ErrorConnector show={hasError} data-sentry-element="ErrorConnector" data-sentry-source-file="File.tsx">
        <Input id={id} aria-label={props["aria-label"]} {...inputProps} ref={fileInputRef} data-sentry-element="Input" data-sentry-source-file="File.tsx" />
        <HelpText data-sentry-element="HelpText" data-sentry-source-file="File.tsx">{t("supportedFileTypeText")}</HelpText>
        {!fileKey ? <SelectFileButton variant="secondary" onClick={event => {
        event.preventDefault();
        if (fileInputRef.current) {
          console.info("Local file browser opened using mouse");
          fileInputRef.current.click();
        }
      }} onKeyUp={event => {
        /**
         * Manually click the file input element when use presses the Enter key.
         * This is for keyboard users and accessibility.
         *
         * Because we have hidden the file input and made this button type='button'
         * we no longer get the submit binding between the button and the file input
         * element when navigating with the keyboard and pressing return.
         */
        if (event.key === "Enter" && fileInputRef.current) {
          console.info("Local file browser opened using keyboard");
          fileInputRef.current.click();
        }
      }}>
            {!loading && <SelectFileLabel htmlFor={id}>{t("selectFile")}</SelectFileLabel>}
            {loading && <LoadingSpinner colour={theme.colours.textOnPrimary} />}
          </SelectFileButton> : <RemoveFileContainer>
            <FileNameText>{fileName}</FileNameText>
            <RemoveFileButton variant="destructive" onClick={event => {
          event.preventDefault();
          onRemoveFile();
          setFileKey(undefined);
        }}>
              <RemoveFileIcon icon={faTrashAlt} /> {t("removeFile")}
            </RemoveFileButton>
          </RemoveFileContainer>}

        {error ? <Error role="alert">{error}</Error> : null}
      </ErrorConnector>
    </>;
}
const Input = styled(BaseInput).attrs({
  type: "file"
})`
  display: none;
`;
const SelectFileLabel = styled.label`
  height: 100%;
  width: 100%;

  @media ${media.hoverable} {
    &:hover {
      cursor: pointer;
    }
  }
`;
const SelectFileButton = styled(Button).attrs({
  type: "button"
})`
  width: 100%;
  ${marginTop.s}
`;
const RemoveFileContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  ${marginTop.s}
`;
const FileNameText = styled.div`
  ${typography.body}
  background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.1)};
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
  ${corners.m}
  ${padding.s}
  ${marginBottom.s}
`;
const RemoveFileButton = styled(Button).attrs({
  type: "button"
})`
  width: 100%;
`;
const RemoveFileIcon = styled(FontAwesomeIcon)`
  ${marginRight.s}
`;
export default File;